"use strict";
/**
 * Continuous typing / body change stream — independent of “draft” UI and pilot_notes.
 *
 * Goal: every keystroke (or map write) lands in a durable ring of history, even if
 * we later discard it and never show a draft pill. Not gated on blur or “dirty vs baseline”.
 *
 * Storage key: audroam.editTyping:{personId}:{mode}:{rowId}
 *
 * Each tick keeps a full body snapshot (reliable recovery without replaying a fragile
 * patch chain). `summary` is a short human diff vs the previous tick for UI later.
 */
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.typingTicksAsDiffTimeline = exports.flushTypingStreamPending = exports.recordTypingBody = exports.summarizeBodyChange = exports.clearTypingStream = exports.readTypingStream = exports.editTypingStorageKey = exports.EDIT_TYPING_FLUSH_MIN_MS = exports.EDIT_TYPING_MAX_TICKS = exports.EDIT_TYPING_VERSION = exports.EDIT_TYPING_PREFIX = void 0;
exports.EDIT_TYPING_PREFIX = 'audroam.editTyping';
exports.EDIT_TYPING_VERSION = 1;
/** Max ticks kept per note (ring). Full body each — keep moderate. */
exports.EDIT_TYPING_MAX_TICKS = 80;
/** Skip write if body unchanged. */
/** Min ms between localStorage flushes when body keeps changing (still coalesce in memory). */
exports.EDIT_TYPING_FLUSH_MIN_MS = 50;
/** In-memory last flush time per key (throttle disk). */
var lastFlushAt = new Map();
/** Pending body waiting for throttle flush */
var pending = new Map();
function editTypingStorageKey(personId, mode, rowId) {
    var pid = personId != null && personId !== '' ? String(personId) : 'anon';
    var rid = rowId != null && rowId !== '' ? String(rowId) : 'unknown';
    return "".concat(exports.EDIT_TYPING_PREFIX, ":").concat(pid, ":").concat(mode, ":").concat(rid);
}
exports.editTypingStorageKey = editTypingStorageKey;
function readTypingStream(storage, personId, mode, rowId) {
    if (!storage)
        return null;
    try {
        var raw = storage.getItem(editTypingStorageKey(personId, mode, rowId));
        if (!raw)
            return null;
        var parsed = JSON.parse(raw);
        if (!parsed || parsed.v !== exports.EDIT_TYPING_VERSION || !Array.isArray(parsed.ticks)) {
            return null;
        }
        return parsed;
    }
    catch (_a) {
        return null;
    }
}
exports.readTypingStream = readTypingStream;
function clearTypingStream(storage, personId, mode, rowId) {
    if (!storage)
        return;
    try {
        var key = editTypingStorageKey(personId, mode, rowId);
        storage.removeItem(key);
        lastFlushAt.delete(key);
        pending.delete(key);
    }
    catch (_a) {
        /* ignore */
    }
}
exports.clearTypingStream = clearTypingStream;
/**
 * Human-readable one-line diff between two body strings (for the tick summary).
 * Not a full patch format — enough to scrub a timeline later.
 */
function summarizeBodyChange(before, after) {
    var a = before == null ? '' : String(before);
    var b = after == null ? '' : String(after);
    if (a === b)
        return '(no change)';
    if (!a && b) {
        var preview = b.length > 48 ? "".concat(b.slice(0, 48), "\u2026") : b;
        return "+".concat(preview.replace(/\s+/g, ' '));
    }
    if (a && !b)
        return '(cleared body)';
    // Common prefix / suffix → middle replacement
    var p = 0;
    var minLen = Math.min(a.length, b.length);
    while (p < minLen && a[p] === b[p])
        p++;
    var sa = a.length - 1;
    var sb = b.length - 1;
    while (sa >= p && sb >= p && a[sa] === b[sb]) {
        sa--;
        sb--;
    }
    var removed = a.slice(p, sa + 1);
    var added = b.slice(p, sb + 1);
    var fmt = function (s) {
        var t = s.replace(/\s+/g, ' ');
        return t.length > 36 ? "".concat(t.slice(0, 36), "\u2026") : t;
    };
    if (removed && added)
        return "-".concat(fmt(removed), " +").concat(fmt(added));
    if (added)
        return "+".concat(fmt(added));
    if (removed)
        return "-".concat(fmt(removed));
    return "len ".concat(a.length, "\u2192").concat(b.length);
}
exports.summarizeBodyChange = summarizeBodyChange;
function writeLog(storage, key, log) {
    try {
        storage.setItem(key, JSON.stringify(log));
        lastFlushAt.set(key, Date.now());
    }
    catch (_a) {
        // Quota: drop older ticks and retry
        try {
            log.ticks = log.ticks.slice(0, Math.max(8, Math.floor(exports.EDIT_TYPING_MAX_TICKS / 4)));
            storage.setItem(key, JSON.stringify(log));
            lastFlushAt.set(key, Date.now());
        }
        catch (_b) {
            /* give up */
        }
    }
}
/**
 * Record a body value into the typing stream (sync-friendly).
 * Throttles localStorage a bit under rapid typing; always updates in-memory pending
 * so the latest keystroke is not lost — call flushTypingStreamPending on blur/emergency.
 */
function recordTypingBody(storage, input) {
    if (!storage || input.rowId === undefined || input.rowId === null) {
        return null;
    }
    var body = input.body == null ? '' : String(input.body);
    var source = input.source || 'type';
    var key = editTypingStorageKey(input.personId, input.mode, input.rowId);
    var personId = input.personId != null ? String(input.personId) : 'anon';
    var rowId = String(input.rowId);
    pending.set(key, {
        body: body,
        source: source,
        personId: personId,
        mode: input.mode,
        rowId: rowId,
    });
    var now = Date.now();
    var last = lastFlushAt.get(key) || 0;
    if (!input.immediate && now - last < exports.EDIT_TYPING_FLUSH_MIN_MS) {
        return readTypingStream(storage, input.personId, input.mode, input.rowId);
    }
    return flushTypingKey(storage, key);
}
exports.recordTypingBody = recordTypingBody;
/** Flush any throttled pending tick for a key (or all keys if key omitted). */
function flushTypingStreamPending(storage, personId, mode, rowId) {
    if (!storage)
        return;
    if (personId !== undefined && mode !== undefined && rowId !== undefined) {
        flushTypingKey(storage, editTypingStorageKey(personId, mode, rowId));
        return;
    }
    pending.forEach(function (_v, key) { return flushTypingKey(storage, key); });
}
exports.flushTypingStreamPending = flushTypingStreamPending;
function flushTypingKey(storage, key) {
    var _a, _b;
    var pend = pending.get(key);
    if (!pend) {
        try {
            var raw = storage.getItem(key);
            return raw ? JSON.parse(raw) : null;
        }
        catch (_c) {
            return null;
        }
    }
    var prev = null;
    try {
        var raw = storage.getItem(key);
        if (raw)
            prev = JSON.parse(raw);
    }
    catch (_d) {
        prev = null;
    }
    var prevBody = prev && typeof prev.latest === 'string'
        ? prev.latest
        : ((_b = (_a = prev === null || prev === void 0 ? void 0 : prev.ticks) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.body) != null
            ? prev.ticks[0].body
            : '';
    if (prevBody === pend.body && prev) {
        // Still refresh latest timestamp lightly? skip disk thrash
        pending.delete(key);
        return prev;
    }
    var at = new Date().toISOString();
    var tick = {
        at: at,
        source: pend.source,
        body: pend.body,
        summary: summarizeBodyChange(prevBody, pend.body),
        len: pend.body.length,
    };
    var ticks = prev && Array.isArray(prev.ticks) ? __spreadArray([], prev.ticks, true) : [];
    ticks.unshift(tick);
    while (ticks.length > exports.EDIT_TYPING_MAX_TICKS) {
        ticks.pop();
    }
    var log = {
        v: exports.EDIT_TYPING_VERSION,
        personId: pend.personId,
        mode: pend.mode,
        rowId: pend.rowId,
        latest: pend.body,
        updatedAt: at,
        ticks: ticks,
    };
    writeLog(storage, key, log);
    pending.delete(key);
    return log;
}
/** Build consecutive diffs for UI (tick[i] vs tick[i+1] older). */
function typingTicksAsDiffTimeline(log) {
    var _a;
    if (!((_a = log === null || log === void 0 ? void 0 : log.ticks) === null || _a === void 0 ? void 0 : _a.length))
        return [];
    return log.ticks.map(function (t) { return ({
        at: t.at,
        source: t.source,
        summary: t.summary,
        len: t.len,
        body: t.body,
    }); });
}
exports.typingTicksAsDiffTimeline = typingTicksAsDiffTimeline;
